import { Inject, Injectable } from '@angular/core';
import { OpportunityService, SimplifiedOpportunity } from '@galaxy/crm/static';
import {
  ListOpportunitiesRequestSortField,
  SalesOpportunitiesService,
  SortDirection,
} from '@vendasta/sales-opportunities';
import { combineLatest, distinctUntilChanged, map, Observable, of, ReplaySubject, switchMap } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PartnerCRMOpportunityService implements OpportunityService {
  private allOpportunities$: Observable<SimplifiedOpportunity[]>;
  private primaryAccountGroupId$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  public primaryAccountGroupId$: Observable<string> = this.primaryAccountGroupId$$.asObservable();

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly salesOpportunityService: SalesOpportunitiesService,
  ) {
    this.allOpportunities$ = this.listOpportunities('', 1000);
  }

  setAccountGroupId(accountGroupId: string): void {
    this.primaryAccountGroupId$$.next(accountGroupId);
  }

  searchOpportunities(searchTerm: string): Observable<SimplifiedOpportunity[]> {
    return this.listOpportunities(searchTerm, 10);
  }

  getMultiOpportunity(opportunityIds: string[]): Observable<SimplifiedOpportunity[]> {
    return this.allOpportunities$.pipe(
      map((opportunities) => opportunities.filter((o) => opportunityIds.includes(o.opportunityId))),
    );
  }

  private listOpportunities(searchTerm: string, pageSize: number): Observable<SimplifiedOpportunity[]> {
    return combineLatest([this.partnerId$, this.primaryAccountGroupId$]).pipe(
      distinctUntilChanged(),
      switchMap(([partnerId, accountGroupId]) => {
        const filters = {
          accountGroupId: accountGroupId,
        };
        const sortOption = {
          field: ListOpportunitiesRequestSortField.LAST_ACTIVITY,
          direction: SortDirection.DESCENDING,
        };
        return this.salesOpportunityService.list(partnerId, '', filters, sortOption, searchTerm, pageSize, '');
      }),
      map((resp) => resp?.results || []),
      map((opportunities) =>
        opportunities.map(
          (opportunity) =>
            ({
              opportunityId: opportunity.opportunityId,
              name: opportunity.name,
            }) as SimplifiedOpportunity,
        ),
      ),
      shareReplay(1),
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  }
}
